/**
 * Storage utility functions for managing tokens and user data
 * Uses localStorage with fallback for security
 */

const STORAGE_KEYS = {
  ACCESS_TOKEN: 'delivery_access_token',
  REFRESH_TOKEN: 'delivery_refresh_token',
  USER_DATA: 'delivery_user_data',
  REMEMBER_ME: 'delivery_remember_me',
}

// ==================== Token Management ====================

/**
 * Save access token to storage
 * @param {string} token - JWT access token
 */
export const setToken = (token) => {
  try {
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, token)
  } catch (error) {
    console.error('Error saving token:', error)
  }
}

/**
 * Get access token from storage
 * @returns {string|null} JWT access token or null
 */
export const getToken = () => {
  try {
    return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN)
  } catch (error) {
    console.error('Error getting token:', error)
    return null
  }
}

/**
 * Remove access token from storage
 */
export const removeToken = () => {
  try {
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN)
  } catch (error) {
    console.error('Error removing token:', error)
  }
}

/**
 * Save refresh token to storage
 * @param {string} token - JWT refresh token
 */
export const setRefreshToken = (token) => {
  try {
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, token)
  } catch (error) {
    console.error('Error saving refresh token:', error)
  }
}

/**
 * Get refresh token from storage
 * @returns {string|null} JWT refresh token or null
 */
export const getRefreshToken = () => {
  try {
    return localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN)
  } catch (error) {
    console.error('Error getting refresh token:', error)
    return null
  }
}

/**
 * Remove refresh token from storage
 */
export const removeRefreshToken = () => {
  try {
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN)
  } catch (error) {
    console.error('Error removing refresh token:', error)
  }
}

// ==================== User Data Management ====================

/**
 * Save user data to storage
 * @param {Object} userData - User object with profile data
 */
export const setUserData = (userData) => {
  try {
    localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(userData))
  } catch (error) {
    console.error('Error saving user data:', error)
  }
}

/**
 * Get user data from storage
 * @returns {Object|null} User data object or null
 */
export const getUserData = () => {
  try {
    const data = localStorage.getItem(STORAGE_KEYS.USER_DATA)
    return data ? JSON.parse(data) : null
  } catch (error) {
    console.error('Error getting user data:', error)
    return null
  }
}

/**
 * Remove user data from storage
 */
export const removeUserData = () => {
  try {
    localStorage.removeItem(STORAGE_KEYS.USER_DATA)
  } catch (error) {
    console.error('Error removing user data:', error)
  }
}

// ==================== Remember Me ====================

/**
 * Save remember me preference
 * @param {boolean} remember - Whether to remember user
 */
export const setRememberMe = (remember) => {
  try {
    localStorage.setItem(STORAGE_KEYS.REMEMBER_ME, String(remember))
  } catch (error) {
    console.error('Error saving remember me:', error)
  }
}

/**
 * Get remember me preference
 * @returns {boolean} Remember me preference
 */
export const getRememberMe = () => {
  try {
    const value = localStorage.getItem(STORAGE_KEYS.REMEMBER_ME)
    return value === 'true'
  } catch (error) {
    console.error('Error getting remember me:', error)
    return false
  }
}

// ==================== Clear All ====================

/**
 * Clear all stored auth data
 * Useful for logout or app reset
 */
export const clearAllAuthData = () => {
  try {
    removeToken()
    removeRefreshToken()
    removeUserData()
    localStorage.removeItem(STORAGE_KEYS.REMEMBER_ME)
  } catch (error) {
    console.error('Error clearing auth data:', error)
  }
}

// ==================== Token Validation ====================

/**
 * Check if access token exists and is not expired
 * @returns {boolean} True if valid token exists
 */
export const hasValidToken = () => {
  const token = getToken()
  if (!token) {
    console.log('🔑 [hasValidToken] No token found')
    return false
  }

  // Laravel Sanctum tokens are plain text (not JWT)
  // Format: "tokenId|plainTextToken"
  // We just check if token exists and has the right format
  if (token.includes('|') && token.length > 10) {
    console.log('🔑 [hasValidToken] Valid Sanctum token found')
    return true
  }

  // Fallback: Try to decode as JWT (for other auth systems)
  try {
    const parts = token.split('.')
    if (parts.length !== 3) {
      console.log('🔑 [hasValidToken] Invalid token format')
      return false
    }

    const payload = JSON.parse(atob(parts[1]))
    const exp = payload.exp

    // Check if token is expired (with 5 minute buffer)
    const now = Math.floor(Date.now() / 1000)
    const buffer = 300 // 5 minutes

    const isValid = exp > (now + buffer)
    console.log('🔑 [hasValidToken] JWT token valid:', isValid)
    return isValid
  } catch (error) {
    console.error('🔑 [hasValidToken] Error validating token:', error)
    return false
  }
}

/**
 * Get token expiry time
 * @returns {Date|null} Token expiry date or null
 */
export const getTokenExpiry = () => {
  const token = getToken()
  if (!token) return null

  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null

    const payload = JSON.parse(atob(parts[1]))
    return new Date(payload.exp * 1000)
  } catch (error) {
    console.error('Error getting token expiry:', error)
    return null
  }
}
